import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/contexts/AuthContext";
import { gcsUploadFile } from '@/lib/gcsStorage';
import { AppShell3 } from "@/components/app-shell-3";
import { AccessDenied } from "@/components/AccessDenied";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
// eslint-disable-next-line no-unused-vars
import { motion } from "framer-motion";
import { ImageCropper } from "@/components/ImageCropper";
import { PageTransition } from "@/components/PageTransition";
import {
  MapPin,
  Building2,
  Calendar,
  Mail,
  Phone,
  Settings,
  Users,
  FileText,
  Globe,
  Shield,
  CreditCard,
  Bell,
  Key,
  UserPlus,
  Trash2,
  Save,
  Upload,
  Pencil,
  Camera,
  Circle,
  Square,
  ChevronRight,
  Database,
  Link2,
  CheckCircle,
  AlertCircle,
  Loader2,
  MessageSquare,
  Smartphone
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { ApiKeysSection } from "./components/ApiKeysSection";
import { CustomProfileTabsManager } from "@/components/CustomProfileTabsManager";

const SECTIONS = [
  { id: 'general', label: 'General', icon: Building2 },
  { id: 'contact', label: 'Contact', icon: Mail },
  { id: 'leave', label: 'Leave Policy', icon: Calendar },
  { id: 'aitables', label: 'AiTables', icon: Database },
  { id: 'profile-tabs', label: 'Profile Tabs', icon: Users },
  { id: 'mobile-app', label: 'Mobile App', icon: Smartphone },
  { id: 'api-keys', label: 'API Keys', icon: Key },
  { id: 'alerts', label: 'Alerts', icon: Bell },
  { id: 'sms', label: 'SMS Credits', icon: MessageSquare },
];

// Function to extract dominant color from image
const extractDominantColor = (imageUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Use a smaller canvas for performance
      const scaleFactor = 50 / Math.max(img.width, img.height);
      canvas.width = img.width * scaleFactor;
      canvas.height = img.height * scaleFactor;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Color buckets for finding dominant color
      const colorMap = {};

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Skip transparent/nearly transparent pixels
        if (a < 128) continue;

        // Skip white, black, and very gray colors
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const diff = max - min;

        // Skip if too close to white, black, or gray
        if (max > 240 && min > 240) continue; // Too close to white
        if (max < 30) continue; // Too close to black
        if (diff < 30) continue; // Too gray

        // Round colors to reduce variations
        const key = `${Math.round(r/10)*10},${Math.round(g/10)*10},${Math.round(b/10)*10}`;
        colorMap[key] = (colorMap[key] || 0) + 1;
      }

      // Find the most common color
      let dominantColor = null;
      let maxCount = 0;

      for (const [color, count] of Object.entries(colorMap)) {
        if (count > maxCount) {
          maxCount = count;
          dominantColor = color;
        }
      }

      // If we found a color, use it; otherwise default to blue
      if (dominantColor) {
        const [r, g, b] = dominantColor.split(',').map(Number);
        resolve({ r, g, b });
      } else {
        // Default blue if no dominant color found
        resolve({ r: 99, g: 102, b: 241 }); // Indigo-500
      }
    };

    img.onerror = () => {
      // Default blue on error
      resolve({ r: 99, g: 102, b: 241 }); // Indigo-500
    };

    img.src = imageUrl;
  });
};

export function CompanySettingsPage() {
  const { user, userProfile, company, can, loading: authLoading, permissionsLoaded, refreshUserData } = useAuth();
  usePageTitle('Company Settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const logoInputRef = useRef(null);
  const [showLogoCropper, setShowLogoCropper] = useState(false);
  const [tempLogoImage, setTempLogoImage] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [bannerColor, setBannerColor] = useState(null);
  const [companyData, setCompanyData] = useState({
    name: '',
    logo_url: '',
    description: '',
    location: '',
    // Additional fields stored in settings
    website: '',
    industry: '',
    size: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    phone: '',
    email: '',
    ai_chat_enabled: true, // AI chat feature toggle
    ai_chat_button_shape: 'circle', // AI chat button shape: 'circle' or 'square'
    mobile_app_name: '',
    mobile_app_icon_url: '',
  });
  const [leaveSettings, setLeaveSettings] = useState({
    default_annual_leave: 25,
    default_sick_leave: 10,
    default_carry_over_days: 0,
    allow_carry_over: true,
    entitled_to_bank_holidays: true,
    working_days: [1, 2, 3, 4, 5] // Mon-Fri default (ISO day numbers: 1=Mon, 7=Sun)
  });
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeProjects: 0,
    totalQualifications: 0,
    complianceRate: 0
  });

  // AiTables Integration state
  const [aiTablesIntegration, setAiTablesIntegration] = useState(null);
  const [aiTablesLoading, setAiTablesLoading] = useState(true);
  const [showAiTablesSetup, setShowAiTablesSetup] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [workspaceNameInput, setWorkspaceNameInput] = useState('');
  const [savingAiTables, setSavingAiTables] = useState(false);

  // Custom Profile Tabs state
  const [customProfileTabs, setCustomProfileTabs] = useState([]);

  // Section navigation
  const [activeSection, setActiveSection] = useState('general');

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of SECTIONS) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // OPTIMIZATION 1: Initialize company data from AuthContext (no refetch needed)
  // This eliminates the redundant company fetch - we already have this data!
  useEffect(() => {
    if (!company) return;

    const settings = company.settings || {};

    setCompanyData({
      name: company.name || '',
      logo_url: company.logo_url || '',
      description: company.description || '',
      location: company.location || '',
      // Additional fields from settings
      website: settings.website || '',
      industry: settings.industry || '',
      size: settings.size || '',
      address: settings.address || '',
      city: settings.city || '',
      state: settings.state || '',
      country: settings.country || '',
      postal_code: settings.postal_code || '',
      phone: settings.phone || '',
      email: settings.email || '',
      ai_chat_enabled: settings.ai_chat_enabled !== false,
      ai_chat_button_shape: settings.ai_chat_button_shape || 'circle',
      mobile_app_name: company.mobile_app_name || '',
      mobile_app_icon_url: company.mobile_app_icon_url || '',
    });

    // Load custom profile tabs from settings
    setCustomProfileTabs(settings.custom_profile_tabs || []);

    // Use cached banner color if available (skip extraction)
    if (settings.bannerColor) {
      setBannerColor(settings.bannerColor);
    }
  }, [company]);

  // OPTIMIZATION 2: Lazy load banner color extraction (only if not cached)
  useEffect(() => {
    if (!company?.logo_url || !company?.id) return;

    const settings = company.settings || {};

    // Skip extraction if already have cached color
    if (settings.bannerColor) return;

    // Extract color only when not cached (fire-and-forget, don't block UI)
    extractDominantColor(company.logo_url).then(color => {
      setBannerColor(color);

      // Save for next time - non-blocking
      supabase
        .from('companies')
        .update({
          settings: { ...settings, bannerColor: color }
        })
        .eq('id', company.id)
        .then(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.logo_url, company?.id, company?.settings?.bannerColor]);

  // OPTIMIZATION 3: Load leave settings and stats in PARALLEL using Promise.all
  // This replaces 7+ sequential queries with 2 parallel queries
  useEffect(() => {
    const loadAdditionalData = async () => {
      if (!company?.id) return;

      try {
        // Run BOTH queries in parallel - much faster than sequential!
        const [leaveSettingsResult, statsResult] = await Promise.all([
          // Query 1: Leave settings
          supabase
            .from('company_leave_settings')
            .select('*')
            .eq('company_id', company.id)
            .single(),

          // Query 2: Dashboard stats via RPC function (replaces 5+ separate queries)
          supabase.rpc('get_company_dashboard_stats', { p_company_id: company.id })
        ]);

        // Process leave settings
        if (!leaveSettingsResult.error && leaveSettingsResult.data) {
          const data = leaveSettingsResult.data;
          setLeaveSettings({
            default_annual_leave: data.default_annual_leave || 25,
            default_sick_leave: data.default_sick_leave || 10,
            default_carry_over_days: data.default_carry_over_days || 0,
            allow_carry_over: data.default_carry_over_days > 0,
            entitled_to_bank_holidays: data.entitled_to_bank_holidays !== false,
            working_days: data.working_days || [1, 2, 3, 4, 5]
          });
        }

        // Process stats from RPC (single efficient database call)
        if (!statsResult.error && statsResult.data) {
          const statsData = statsResult.data;
          setStats({
            totalMembers: statsData.total_members || 0,
            activeProjects: statsData.active_projects || 0,
            totalQualifications: statsData.total_requirements || 0,
            complianceRate: statsData.compliance_rate || 0
          });
        }
      } catch (error) {
        console.error('Error loading company data:', error);
        toast.error('Failed to load company data');
      } finally {
        setLoading(false);
      }
    };

    loadAdditionalData();
  }, [company?.id]);

  // Fetch AiTables integration status
  useEffect(() => {
    const fetchAiTablesStatus = async () => {
      if (!company?.id) return;

      setAiTablesLoading(true);
      try {
        const { getIntegrationStatus } = await import('@/lib/aitablesService');
        const result = await getIntegrationStatus(company.id);
        setAiTablesIntegration(result.integration);
      } catch (error) {
        console.error('Error fetching AiTables integration:', error);
      } finally {
        setAiTablesLoading(false);
      }
    };

    fetchAiTablesStatus();
  }, [company?.id]);

  // AiTables integration handlers
  const handleSaveAiTablesIntegration = async () => {
    if (!apiKeyInput.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setSavingAiTables(true);
    try {
      const { validateAndSaveApiKey } = await import('@/lib/aitablesService');
      const result = await validateAndSaveApiKey(company.id, apiKeyInput.trim(), workspaceNameInput.trim());

      if (!result.valid) {
        toast.error(result.error || 'Invalid API key');
        return;
      }

      toast.success('AiTables integration connected successfully');
      setShowAiTablesSetup(false);
      setApiKeyInput('');
      setWorkspaceNameInput('');

      // Refresh integration status
      const { getIntegrationStatus } = await import('@/lib/aitablesService');
      const status = await getIntegrationStatus(company.id);
      setAiTablesIntegration(status.integration);
    } catch (error) {
      console.error('Error saving AiTables integration:', error);
      toast.error('Failed to save integration');
    } finally {
      setSavingAiTables(false);
    }
  };

  const handleRemoveAiTablesIntegration = async () => {
    if (!confirm('Are you sure you want to remove the AiTables integration? This will disconnect all AiTables data sources.')) {
      return;
    }

    setSavingAiTables(true);
    try {
      const { removeIntegration } = await import('@/lib/aitablesService');
      await removeIntegration(company.id);
      toast.success('AiTables integration removed');
      setAiTablesIntegration(null);
    } catch (error) {
      console.error('Error removing AiTables integration:', error);
      toast.error('Failed to remove integration');
    } finally {
      setSavingAiTables(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCompanyData(prev => {
      const updated = { ...prev, [field]: value };

      // Update location when city or state changes
      if (field === 'city' || field === 'state') {
        const locationParts = [
          field === 'city' ? value : prev.city,
          field === 'state' ? value : prev.state
        ].filter(Boolean);
        updated.location = locationParts.length > 0 ? locationParts.join(', ') : '';
      }

      return updated;
    });
  };

  const toggleWorkingDay = (dayValue) => {
    setLeaveSettings(prev => {
      const currentDays = prev.working_days || [1, 2, 3, 4, 5];
      let newDays;

      if (currentDays.includes(dayValue)) {
        // Remove the day
        newDays = currentDays.filter(d => d !== dayValue);
        // Prevent empty array - require at least one working day
        if (newDays.length === 0) {
          toast.error('At least one working day is required');
          return prev;
        }
      } else {
        // Add the day and sort
        newDays = [...currentDays, dayValue].sort((a, b) => a - b);
      }

      return { ...prev, working_days: newDays };
    });
  };

  const handleLogoImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempLogoImage(reader.result);
        setShowLogoCropper(true);
      };
      reader.onerror = (error) => {
        console.error('Error reading logo file:', error);
        toast.error('Failed to read image file');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoCropComplete = async (croppedImage) => {
    if (!company?.id) return;

    setUploadingLogo(true);
    try {
      // Convert base64 to blob
      const base64Response = await fetch(croppedImage);
      const blob = await base64Response.blob();

      // Upload to GCS
      const { publicUrl } = await gcsUploadFile(blob, {
        folderPath: `avatars/company-logos`,
        fileName: `${company.id}-${Date.now()}.png`,
        contentType: 'image/png'
      });

      // Update company logo URL
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          logo_url: publicUrl,
          updated_date: new Date().toISOString()
        })
        .eq('id', company.id);

      if (updateError) throw updateError;

      // Update local state
      setCompanyData(prev => ({ ...prev, logo_url: publicUrl }));

      // Extract and apply dominant color from new logo
      const color = await extractDominantColor(publicUrl);
      setBannerColor(color);

      // Save color to settings
      const { data: currentCompany } = await supabase
        .from('companies')
        .select('settings')
        .eq('id', company.id)
        .single();

      await supabase
        .from('companies')
        .update({
          settings: {
            ...(currentCompany?.settings || {}),
            bannerColor: color
          }
        })
        .eq('id', company.id);

      toast.success('Company logo updated successfully');
      refreshUserData();
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!company?.id) return;

    setSaving(true);
    try {
      // Build location string from city and state
      const locationParts = [companyData.city, companyData.state].filter(Boolean);
      const location = locationParts.length > 0 ? locationParts.join(', ') : '';

      // Build settings object for JSONB field
      const settings = {
        website: companyData.website,
        industry: companyData.industry,
        size: companyData.size,
        address: companyData.address,
        city: companyData.city,
        state: companyData.state,
        country: companyData.country,
        postal_code: companyData.postal_code,
        phone: companyData.phone,
        email: companyData.email,
        ai_chat_enabled: companyData.ai_chat_enabled,
        ai_chat_button_shape: companyData.ai_chat_button_shape,
        custom_profile_tabs: customProfileTabs,
      };

      const { error } = await supabase
        .from('companies')
        .update({
          name: companyData.name,
          logo_url: companyData.logo_url,
          description: companyData.description,
          location: location,
          settings: settings,
          mobile_app_name: companyData.mobile_app_name || null,
          mobile_app_icon_url: companyData.mobile_app_icon_url || null,
          updated_date: new Date().toISOString()
        })
        .eq('id', company.id);

      if (error) throw error;

      // Save leave settings
      const { error: leaveError } = await supabase
        .from('company_leave_settings')
        .upsert({
          company_id: company.id,
          default_annual_leave: leaveSettings.default_annual_leave,
          default_sick_leave: leaveSettings.default_sick_leave,
          default_carry_over_days: leaveSettings.allow_carry_over ? leaveSettings.default_carry_over_days : 0,
          entitled_to_bank_holidays: leaveSettings.entitled_to_bank_holidays,
          working_days: leaveSettings.working_days,
          updated_date: new Date().toISOString()
        }, {
          onConflict: 'company_id'
        });

      if (leaveError) throw leaveError;

      toast.success('Company settings updated successfully');
      refreshUserData();
    } catch (error) {
      console.error('Error saving company data:', error);
      toast.error('Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCompanyInitials = () => {
    if (companyData.name) {
      return companyData.name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase();
    }
    return 'CO';
  };

  // Show loading while auth is loading or permissions haven't been fetched yet
  if (authLoading || !permissionsLoaded) {
    return (
      <AppShell3>
        <div className="min-h-[50vh] flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  // Check permission after auth and permissions are fully loaded
  const canAccessCompanySettings = can('company.settings');

  // Show access denied if user doesn't have permission
  if (!canAccessCompanySettings) {
    return (
      <AppShell3>
        <AccessDenied
          message="You don't have permission to access company settings. Only admins can access this page."
          backTo="/dashboard"
          backLabel="Back to Dashboard"
        />
      </AppShell3>
    );
  }

  if (loading) {
    return (
      <AppShell3>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="min-h-screen bg-gray-50"
        >
          {/* Company Header Skeleton */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start sm:space-x-6">
                {/* Logo Skeleton */}
                <div className="h-32 w-32 bg-gray-200 rounded-full border-4 border-white shadow-lg animate-pulse flex-shrink-0" />

                {/* Company Details Skeleton */}
                <div className="mt-4 sm:mt-0 text-center sm:text-left flex-1 space-y-3">
                  <div className="h-8 bg-gray-200 rounded w-48 mx-auto sm:mx-0 animate-pulse" />
                  <div className="h-5 bg-gray-200 rounded w-32 mx-auto sm:mx-0 animate-pulse" />
                  <div className="flex flex-wrap gap-4 mt-4 justify-center sm:justify-start">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Settings Section Skeleton */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Settings Cards Skeleton */}
            <div className="grid gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <div className="h-6 bg-gray-200 rounded w-48 mb-2 animate-pulse" />
                    <div className="h-4 bg-gray-200 rounded w-64 animate-pulse" />
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[1, 2].map((j) => (
                        <div key={j} className="space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
                          <div className="h-10 bg-gray-200 rounded animate-pulse" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </motion.div>
      </AppShell3>
    );
  }

  return (
    <AppShell3>
      <PageTransition>
        <div className="min-h-screen bg-gray-50">
          {/* Company Header */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start sm:space-x-6">
                {/* Company Logo with Edit Overlay */}
                <div className="relative group flex-shrink-0">
                  <Avatar className="h-32 w-32 border-4 border-white shadow-lg">
                    <AvatarImage
                      src={companyData.logo_url}
                      alt={companyData.name}
                    />
                    <AvatarFallback className="text-2xl font-semibold bg-indigo-100 text-indigo-700">
                      {getCompanyInitials()}
                    </AvatarFallback>
                  </Avatar>

                  {/* Edit Overlay */}
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
                    ) : (
                      <Pencil className="h-4 w-4 text-gray-700" />
                    )}
                  </button>

                  {/* Hidden File Input */}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoImageChange}
                    className="hidden"
                  />
                </div>

                {/* Company Details */}
                <div className="mt-4 sm:mt-0 text-center sm:text-left flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900">
                        {companyData.name || 'Company Name'}
                      </h1>
                      <p className="text-lg text-gray-600 mt-1">
                        {companyData.industry || 'Industry'}
                      </p>
                    </div>
                  </div>

                  {/* Meta Information */}
                  <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-600">
                    {companyData.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {companyData.location}
                      </div>
                    )}
                    {companyData.website && (
                      <div className="flex items-center gap-1">
                        <Globe className="h-4 w-4" />
                        {companyData.website}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {stats.totalMembers} Members
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Created {formatDate(company?.created_date)}
                    </div>
                  </div>

                  {/* Stats Badges */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Badge variant="outline" className="gap-1.5 px-2.5 py-1 text-xs font-medium">
                      <FileText className="h-3 w-3 text-blue-500" />
                      {stats.activeProjects} Projects
                    </Badge>
                    <Badge variant="outline" className="gap-1.5 px-2.5 py-1 text-xs font-medium">
                      <Shield className="h-3 w-3 text-green-500" />
                      {stats.totalQualifications} Qualifications
                    </Badge>
                    <Badge variant="outline" className="gap-1.5 px-2.5 py-1 text-xs font-medium">
                      <Building2 className="h-3 w-3 text-purple-500" />
                      {stats.complianceRate}% Compliance
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Settings Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-8">
          {/* Sticky Section Navigation */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <nav className="sticky top-24 space-y-1">
              {SECTIONS.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                    {section.label}
                  </button>
                );
              })}
              <div className="mt-4 pt-4 border-t">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full shadow-sm"
                >
                  {saving ? (
                    <>
                      <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </nav>
          </div>

          {/* Settings Sections */}
          <div className="flex-1 min-w-0 grid gap-8">
            {/* General Information */}
            <Card id="general" className="scroll-mt-24">
              <CardHeader>
                <CardTitle>General Information</CardTitle>
                <CardDescription>Basic information about your company</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Company Name</Label>
                    <Input
                      id="company-name"
                      value={companyData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Input
                      id="industry"
                      value={companyData.industry}
                      onChange={(e) => handleInputChange('industry', e.target.value)}
                      placeholder="e.g., Construction, Healthcare"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={companyData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="size">Company Size</Label>
                    <Input
                      id="size"
                      value={companyData.size}
                      onChange={(e) => handleInputChange('size', e.target.value)}
                      placeholder="e.g., 50-100 employees"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={companyData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Brief description of your company"
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card id="contact" className="scroll-mt-24">
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>Company contact details and address</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Contact Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={companyData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={companyData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+44 20 7123 4567"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={companyData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="123 High Street"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={companyData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="London"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">County</Label>
                    <Input
                      id="state"
                      value={companyData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      placeholder="Greater London"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">Postcode</Label>
                    <Input
                      id="postal_code"
                      value={companyData.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      placeholder="SW1A 1AA"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={companyData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      placeholder="United Kingdom"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Leave Policy Settings */}
            <Card id="leave" className="scroll-mt-24">
              <CardHeader>
                <CardTitle>Leave Policy</CardTitle>
                <CardDescription>Default leave entitlements for new employees</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="annual-leave">Annual Leave Days</Label>
                  <Input
                    id="annual-leave"
                    type="number"
                    min="0"
                    value={leaveSettings.default_annual_leave}
                    onChange={(e) => setLeaveSettings(prev => ({ ...prev, default_annual_leave: parseFloat(e.target.value) || 0 }))}
                    placeholder="25"
                  />
                  <p className="text-xs text-muted-foreground">Number of paid annual leave days per year</p>
                </div>

                {/* Company Working Days */}
                <div className="space-y-3 p-4 rounded-lg border bg-muted/20">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Company Working Days</Label>
                    <p className="text-sm text-muted-foreground">
                      Select which days your company typically operates. This affects how leave days are calculated.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { day: 'Mon', value: 1 },
                      { day: 'Tue', value: 2 },
                      { day: 'Wed', value: 3 },
                      { day: 'Thu', value: 4 },
                      { day: 'Fri', value: 5 },
                      { day: 'Sat', value: 6 },
                      { day: 'Sun', value: 7 }
                    ].map(({ day, value }) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleWorkingDay(value)}
                        className={`w-12 h-12 rounded-lg border-2 font-medium transition-all ${
                          leaveSettings.working_days?.includes(value)
                            ? 'bg-violet-600 text-white border-violet-600'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Individual employees can have different patterns set in their attendance profile.
                  </p>
                </div>

                <div className="flex items-center justify-between space-x-2 p-4 rounded-lg border bg-muted/20">
                  <div className="space-y-0.5">
                    <Label htmlFor="allow-carry-over" className="text-base font-medium">
                      Allow Carry Over
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Allow employees to carry unused leave days to the next year
                    </p>
                  </div>
                  <Switch
                    id="allow-carry-over"
                    checked={leaveSettings.allow_carry_over}
                    onCheckedChange={(checked) => setLeaveSettings(prev => ({ ...prev, allow_carry_over: checked }))}
                  />
                </div>

                {leaveSettings.allow_carry_over && (
                  <div className="space-y-2">
                    <Label htmlFor="carry-over">Maximum Carry Over Days</Label>
                    <Input
                      id="carry-over"
                      type="number"
                      min="0"
                      value={leaveSettings.default_carry_over_days}
                      onChange={(e) => setLeaveSettings(prev => ({ ...prev, default_carry_over_days: parseFloat(e.target.value) || 0 }))}
                      placeholder="5"
                    />
                    <p className="text-xs text-muted-foreground">Maximum days that can be carried over from previous year</p>
                  </div>
                )}

                <div className="flex items-center justify-between space-x-2 p-4 rounded-lg border bg-muted/20">
                  <div className="space-y-0.5">
                    <Label htmlFor="bank-holidays" className="text-base font-medium">
                      Bank Holidays
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Employees are entitled to standard bank holidays in addition to annual leave
                    </p>
                  </div>
                  <Switch
                    id="bank-holidays"
                    checked={leaveSettings.entitled_to_bank_holidays}
                    onCheckedChange={(checked) => setLeaveSettings(prev => ({ ...prev, entitled_to_bank_holidays: checked }))}
                  />
                </div>
                <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                  <div className="flex items-start gap-2">
                    <Bell className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Automatic Entitlements</p>
                      <p className="text-xs text-blue-600">
                        These settings are automatically applied when new users join your company. Existing users keep their current entitlements unless manually updated. Individual entitlements (including bank holiday eligibility) can be overridden per user in the Entitlements page.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Features & Preferences - Only show for tom+1@add.ai */}
            {user?.email === 'tom+1@add.ai' && (
              <Card>
                <CardHeader>
                  <CardTitle>Features & Preferences</CardTitle>
                  <CardDescription>Manage advanced features and settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* +AI Assistant Toggle */}
                  <div className="flex items-center justify-between space-x-2 p-4 rounded-lg border bg-muted/20">
                    <div className="space-y-0.5">
                      <Label htmlFor="ai-chat" className="text-base font-medium">
                        +AI Assistant
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Enable the AI-powered assistant to help navigate the platform and answer questions
                      </p>
                    </div>
                    <Switch
                      id="ai-chat"
                      checked={companyData.ai_chat_enabled}
                      onCheckedChange={(checked) => handleInputChange('ai_chat_enabled', checked)}
                    />
                  </div>

                  {companyData.ai_chat_enabled && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-4"
                    >
                      <div className="ml-4 p-3 rounded-md bg-blue-50 border border-blue-200">
                        <div className="flex items-start gap-2">
                          <Bell className="h-4 w-4 text-blue-600 mt-0.5" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">+AI Assistant Active</p>
                            <p className="text-xs text-blue-600">
                              The +AI assistant button will appear in the bottom right corner of all pages.
                              Users can ask questions about compliance, training requirements, and platform features.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Button Shape Selector */}
                      <div className="ml-4 space-y-2">
                        <Label className="text-sm font-medium">Button Shape</Label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => handleInputChange('ai_chat_button_shape', 'circle')}
                            className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                              companyData.ai_chat_button_shape === 'circle'
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            <Circle className="h-5 w-5" />
                            <span className="text-sm font-medium">Circle</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleInputChange('ai_chat_button_shape', 'square')}
                            className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                              companyData.ai_chat_button_shape === 'square'
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            <Square className="h-5 w-5" />
                            <span className="text-sm font-medium">Rounded Square</span>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* AiTables Integration */}
            <Card id="aitables" className="scroll-mt-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  AiTables Integration
                </CardTitle>
                <CardDescription>
                  Connect to AiTables to use dynamic data sources in workflows and forms
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {aiTablesLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Checking integration status...</span>
                  </div>
                ) : aiTablesIntegration?.isActive ? (
                  // Integration is connected
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div className="flex-1">
                        <p className="font-medium text-green-800">Connected</p>
                        {aiTablesIntegration.workspaceName && (
                          <p className="text-sm text-green-600">
                            Workspace: {aiTablesIntegration.workspaceName}
                          </p>
                        )}
                        {aiTablesIntegration.lastVerifiedAt && (
                          <p className="text-xs text-green-500">
                            Last verified: {new Date(aiTablesIntegration.lastVerifiedAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      onClick={handleRemoveAiTablesIntegration}
                      disabled={savingAiTables}
                    >
                      {savingAiTables ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      Disconnect Integration
                    </Button>
                  </div>
                ) : showAiTablesSetup ? (
                  // Setup form
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="workspace-name">Workspace Name (optional)</Label>
                      <Input
                        id="workspace-name"
                        placeholder="e.g., My Company Workspace"
                        value={workspaceNameInput}
                        onChange={(e) => setWorkspaceNameInput(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="api-key">API Key</Label>
                      <Input
                        id="api-key"
                        type="password"
                        placeholder="Enter your AiTables API key"
                        value={apiKeyInput}
                        onChange={(e) => setApiKeyInput(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Your API key is stored securely and never exposed to the browser.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveAiTablesIntegration}
                        disabled={savingAiTables || !apiKeyInput.trim()}
                      >
                        {savingAiTables ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <Link2 className="h-4 w-4 mr-2" />
                            Connect
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowAiTablesSetup(false);
                          setApiKeyInput('');
                          setWorkspaceNameInput('');
                        }}
                        disabled={savingAiTables}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Not connected - show setup button
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-muted/50 border border-muted rounded-lg">
                      <AlertCircle className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="font-medium">Not Connected</p>
                        <p className="text-sm text-muted-foreground">
                          Connect to AiTables to enable dynamic dropdown options in your workflows
                        </p>
                      </div>
                    </div>
                    <Button onClick={() => setShowAiTablesSetup(true)}>
                      <Link2 className="h-4 w-4 mr-2" />
                      Connect AiTables
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Custom Profile Tabs */}
            <Card id="profile-tabs" className="scroll-mt-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Profile Tabs
                </CardTitle>
                <CardDescription>
                  Add custom embedded tabs to user profile pages. These tabs appear for all users in the company and can display external content with user-specific data.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CustomProfileTabsManager
                  customTabs={customProfileTabs}
                  onChange={setCustomProfileTabs}
                />
              </CardContent>
            </Card>

            {/* Mobile App Branding */}
            <Card id="mobile-app" className="scroll-mt-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Mobile App Branding
                </CardTitle>
                <CardDescription>
                  Customize the name and icon employees see when they install the mobile app on their phone.
                  If left blank, the default "My Training" branding is used.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mobile-app-name">App Name</Label>
                  <Input
                    id="mobile-app-name"
                    placeholder="e.g. Elan Training"
                    value={companyData.mobile_app_name}
                    onChange={(e) => handleInputChange('mobile_app_name', e.target.value)}
                    maxLength={30}
                  />
                  <p className="text-xs text-muted-foreground">
                    Shown on the home screen icon. Keep it short (under 15 characters is best).
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>App Icon</Label>
                  <div className="flex items-center gap-4">
                    {companyData.mobile_app_icon_url && (
                      <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white border border-gray-200 flex-shrink-0">
                        <img src={companyData.mobile_app_icon_url} alt="App icon" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('mobile-icon-upload').click()}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          {companyData.mobile_app_icon_url ? 'Change Icon' : 'Upload Icon'}
                        </Button>
                        {companyData.mobile_app_icon_url && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleInputChange('mobile_app_icon_url', '')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Square PNG, minimum 512x512px. Leave blank to use the default icon.
                      </p>
                    </div>
                  </div>
                  <input
                    id="mobile-icon-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        toast.info('Uploading icon...');
                        const { publicUrl } = await gcsUploadFile(file, {
                          folderPath: `companies/${company.id}/mobile`,
                          fileName: `app-icon-${Date.now()}.${file.name.split('.').pop()}`,
                        });
                        handleInputChange('mobile_app_icon_url', publicUrl);
                        toast.success('Icon uploaded');
                      } catch (err) {
                        console.error('Icon upload failed:', err);
                        toast.error('Failed to upload icon');
                      }
                      e.target.value = '';
                    }}
                  />
                </div>

                {(companyData.mobile_app_name || companyData.mobile_app_icon_url) && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-emerald-800 mb-2">Preview</p>
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl overflow-hidden bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
                        {companyData.mobile_app_icon_url ? (
                          <img
                            src={companyData.mobile_app_icon_url}
                            alt="App icon"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div className={`w-full h-full bg-emerald-500 items-center justify-center text-white text-lg font-bold ${companyData.mobile_app_icon_url ? 'hidden' : 'flex'}`}>
                          {(companyData.mobile_app_name || companyData.name || 'M')[0]?.toUpperCase()}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {companyData.mobile_app_name || 'My Training'}
                        </p>
                        <p className="text-xs text-gray-500">Home screen icon</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* API Keys Management */}
            <div id="api-keys" className="scroll-mt-24">
              <ApiKeysSection company={company} />
            </div>

            {/* Alert Settings Link */}
            <Card id="alerts" className="scroll-mt-24 hover:shadow-md transition-shadow cursor-pointer">
              <Link to="/company-settings/alerts" onClick={() => window.scrollTo(0, 0)}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-indigo-100 rounded-lg">
                        <Bell className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">Company Alerts</h3>
                        <p className="text-sm text-muted-foreground">
                          Configure notification channels, alert preferences, and thresholds for Admins and Managers
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </CardContent>
              </Link>
            </Card>

            {/* SMS Credits Link */}
            <Card id="sms" className="scroll-mt-24 hover:shadow-md transition-shadow cursor-pointer">
              <Link to="/company-settings/sms-credits" onClick={() => window.scrollTo(0, 0)}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-cyan-100 rounded-lg">
                        <MessageSquare className="h-6 w-6 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">SMS Credits</h3>
                        <p className="text-sm text-muted-foreground">
                          Send SMS messages to team members, manage credits, and view message history
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </CardContent>
              </Link>
            </Card>
          </div>
          </div>
        </div>


        {/* Logo Image Cropper */}
        <ImageCropper
          isOpen={showLogoCropper}
          onClose={() => {
            setShowLogoCropper(false);
            setTempLogoImage(null);
          }}
          imageSrc={tempLogoImage}
          onCropComplete={(croppedImage) => {
            handleLogoCropComplete(croppedImage);
            setShowLogoCropper(false);
            setTempLogoImage(null);
          }}
          aspectRatio={1}
          cropShape="rect"
        />
        </div>
      </PageTransition>
    </AppShell3>
  );
}